import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import MapView from '@/components/MapView';
import { getImageUrl, formatPrice } from '@/components/PropertyCard';
import AdBanner from '@/components/AdBanner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Bed, Bath, Maximize, MapPin, Eye, Heart, Phone, Mail, Send, ChevronLeft, ChevronRight, User, Droplets, Zap, Wifi, Video, FileText, Check, CheckCircle2, ShieldCheck, DollarSign, Lock, Crown, Building2, Loader2 } from 'lucide-react';
import { useTracker } from '@/hooks/useTracker';
import { getUtilityLabel } from '@/lib/utilities';
import VerifiedBadge from '@/components/VerifiedBadge';
import ShareDialog from '@/components/ShareDialog';
import PropertyQRPoster from '@/components/PropertyQRPoster';
import TrustScore from '@/components/TrustScore';
import InstagramEmbeds from '@/components/InstagramEmbeds';
import PlatformDisclaimer from '@/components/PlatformDisclaimer';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PropertyDetailPage() {
  const { id } = useParams();
  const { t, language } = useLanguage();
  const { user, token, isAuthenticated } = useAuth();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImage, setCurrentImage] = useState(0);
  const [contactForm, setContactForm] = useState({ sender_name: '', sender_email: '', sender_phone: '', message: '' });
  const [sending, setSending] = useState(false);
  const [isFav, setIsFav] = useState(false);
  const [ownerVerification, setOwnerVerification] = useState(null);
  const [propVerification, setPropVerification] = useState(null);
  const [revealedContact, setRevealedContact] = useState(null);
  const [revealing, setRevealing] = useState(false);
  const { track, startTimer, stopTimer } = useTracker(token);

  const handleRevealContact = async () => {
    setRevealing(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const r = await axios.post(`${API}/properties/${id}/reveal-contact`, {}, { headers });
      setRevealedContact(r.data);
      track('property_contact_revealed', { property_id: id });
    } catch (err) {
      const detail = err?.response?.data?.detail || (language === 'es' ? 'Error al mostrar contacto' : 'Could not reveal contact');
      toast.error(detail);
    } finally {
      setRevealing(false);
    }
  };

  useEffect(() => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    axios.get(`${API}/properties/${id}`, { headers })
      .then(res => {
        setProperty(res.data);
        setIsFav(user?.favorites?.includes(res.data.id));
        startTimer(id);
        // Update browser tab title for nicer in-app UX
        if (res.data?.title) {
          document.title = `${res.data.title} — WakaCasa`;
        }
        // Increment per-user property-view counter and fire feedback on 10th
        if (user?.id) {
          const key = `waka-views-${user.id}`;
          const n = (parseInt(localStorage.getItem(key) || '0', 10) || 0) + 1;
          localStorage.setItem(key, String(n));
          if (n === 10) {
            setTimeout(() => { try { window.__wakaFeedback?.fire?.('10th_property_view'); } catch {} }, 2000);
          }
        }
        // Fetch property verification (lightweight; backend gates docs by viewer)
        axios.get(`${API}/property-verification/property/${id}`, { headers })
          .then(pvr => setPropVerification(pvr.data))
          .catch(() => {});
        // Fetch owner verification status
        if (res.data.owner_id) {
          axios.get(`${API}/verification/public/${res.data.owner_id}`)
            .then(vr => setOwnerVerification(vr.data))
            .catch(() => {});
        }
      })
      .catch(() => toast.error('Property not found'))
      .finally(() => setLoading(false));
    return () => {
      stopTimer(id);
      document.title = 'WakaCasa';
    };
  }, [id, user, token]);

  const allImages = property ? [property.main_photo, ...(property.gallery || [])].filter(Boolean) : [];

  const handleContact = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/contact`, { ...contactForm, property_id: id }, { headers });
      track('contact_click', { property_id: id });
      toast.success(t('contact.success'));
      setContactForm({ sender_name: '', sender_email: '', sender_phone: '', message: '' });
    } catch { toast.error(t('common.error')); }
    finally { setSending(false); }
  };

  const toggleFav = async () => {
    if (!isAuthenticated) { toast.error('Please sign in'); return; }
    try {
      if (isFav) {
        await axios.delete(`${API}/users/favorites/${id}`, { headers: { Authorization: `Bearer ${token}` } });
        setIsFav(false);
      } else {
        await axios.post(`${API}/users/favorites/${id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
        setIsFav(true);
      }
    } catch { toast.error('Error'); }
  };

  if (loading) return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Skeleton className="h-8 w-48 mb-6" /><Skeleton className="h-[400px] rounded-xl mb-6" />
      <div className="grid lg:grid-cols-3 gap-6"><div className="lg:col-span-2 space-y-4"><Skeleton className="h-10 w-3/4" /><Skeleton className="h-6 w-1/2" /><Skeleton className="h-32" /></div><Skeleton className="h-64" /></div>
    </div>
  );

  if (!property) return <div className="text-center py-20 text-slate-500">Property not found</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="property-detail-page">
      {/* Back */}
      <Link to="/search" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-primary mb-4 transition-colors" data-testid="back-to-search">
        <ArrowLeft className="w-4 h-4" />{t('common.back')}
      </Link>

      {/* Gallery */}
      <div className="relative rounded-2xl overflow-hidden bg-slate-100 mb-6" data-testid="property-gallery">
        {allImages.length > 0 ? (
          <div className="relative aspect-[16/9] md:aspect-[2.2/1]">
            <img src={getImageUrl(allImages[currentImage], currentImage)} alt={property.title}
              className="w-full h-full object-cover" />
            {allImages.length > 1 && (
              <>
                <button onClick={() => setCurrentImage(i => (i - 1 + allImages.length) % allImages.length)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow-md hover:bg-white"
                  data-testid="gallery-prev"><ChevronLeft className="w-5 h-5" /></button>
                <button onClick={() => setCurrentImage(i => (i + 1) % allImages.length)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow-md hover:bg-white"
                  data-testid="gallery-next"><ChevronRight className="w-5 h-5" /></button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {allImages.map((_, i) => (
                    <button key={i} onClick={() => setCurrentImage(i)}
                      className={`w-2 h-2 rounded-full transition-colors ${i === currentImage ? 'bg-white' : 'bg-white/50'}`} />
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="aspect-[16/9] md:aspect-[2.2/1] bg-slate-200 flex items-center justify-center text-slate-400">No images</div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Title & Price */}
          <div>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-primary text-white">{t(`types.${property.property_type}`)}</Badge>
                  <Badge className={property.operation_type === 'rent' ? 'bg-teal-600 text-white' : 'bg-[#D4AF37] text-white'}>
                    {t(`operations.${property.operation_type}`)}
                  </Badge>
                  <Badge variant="outline" className="gap-1"><Eye className="w-3 h-3" />{property.views_count}</Badge>
                </div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900" data-testid="property-title">{property.title}</h1>
                <div className="flex items-center gap-1 mt-1 text-slate-500">
                  <MapPin className="w-4 h-4" />
                  <span>{property.city}{property.neighborhood ? `, ${property.neighborhood}` : ''}</span>
                </div>
                {/* WakaCode + Agency Reference */}
                {(property.wakacode || property.reference) && (
                  <div className="flex flex-wrap items-center gap-2 mt-2.5" data-testid="property-codes">
                    {property.wakacode && (
                      <button
                        type="button"
                        onClick={() => {
                          if (navigator.clipboard) {
                            navigator.clipboard.writeText(property.wakacode);
                            toast.success(language === 'es' ? 'WakaCode copiado' : 'WakaCode copied');
                          }
                        }}
                        title={language === 'es' ? 'Copiar WakaCode' : 'Copy WakaCode'}
                        className="inline-flex items-center gap-1.5 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-2.5 py-1 text-xs font-mono tracking-wider text-[#0B192C] hover:bg-[#D4AF37]/20 transition"
                        data-testid="property-wakacode"
                      >
                        <ShieldCheck className="w-3 h-3 text-[#D4AF37]" />
                        <span className="text-[10px] uppercase tracking-wider text-[#D4AF37] font-semibold not-italic font-sans">WakaCode</span>
                        <span>{property.wakacode}</span>
                      </button>
                    )}
                    {property.reference && (
                      <span
                        className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-50 px-2.5 py-1 text-xs text-slate-700"
                        data-testid="property-reference"
                      >
                        <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{language === 'es' ? 'Ref.' : 'Ref.'}</span>
                        <span className="font-mono">{property.reference}</span>
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="text-right flex-shrink-0">
                <div className="font-heading text-2xl md:text-3xl font-bold text-primary" data-testid="property-price">
                  {formatPrice(property.price, property.currency, property.operation_type)}
                </div>
                <div className="mt-2 flex items-center justify-end gap-3">
                  <button onClick={toggleFav} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-red-500" data-testid="detail-favorite-btn">
                    <Heart className={`w-4 h-4 ${isFav ? 'fill-red-500 text-red-500' : ''}`} />
                    {isFav ? 'Saved' : 'Save'}
                  </button>
                  <ShareDialog
                    url={`${process.env.REACT_APP_BACKEND_URL}/api/share/property/${property.id}`}
                    title={property.title}
                    description={`${property.city}${property.neighborhood ? ', ' + property.neighborhood : ''} • ${formatPrice(property.price, property.currency, property.operation_type)}`}
                    triggerLabel={language === 'es' ? 'Compartir' : 'Share'}
                    triggerVariant="ghost"
                    triggerSize="sm"
                    triggerClassName="h-7 px-2 text-sm text-slate-500 hover:text-[#0B192C]"
                    testIdPrefix="detail-share"
                  />
                  {/* QR Poster — owner / admin / agency-mate only */}
                  {user && (
                    user.id === property.owner_id ||
                    ['admin', 'superadmin'].includes(user.role) ||
                    (user.role === 'agent' && user.agency_id && property.agency_id === user.agency_id)
                  ) && (
                    <PropertyQRPoster property={property} triggerSize="sm" triggerVariant="ghost" />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Features */}
          {property.property_type !== 'land' && (
            <div className="flex gap-6 py-4 border-y border-border">
              {property.bedrooms > 0 && <div className="flex items-center gap-2"><Bed className="w-5 h-5 text-primary" /><div><div className="font-semibold">{property.bedrooms}</div><div className="text-xs text-slate-500">{t('property.bedrooms')}</div></div></div>}
              {property.bathrooms > 0 && <div className="flex items-center gap-2"><Bath className="w-5 h-5 text-primary" /><div><div className="font-semibold">{property.bathrooms}</div><div className="text-xs text-slate-500">{t('property.bathrooms')}</div></div></div>}
              <div className="flex items-center gap-2"><Maximize className="w-5 h-5 text-primary" /><div><div className="font-semibold">{property.area} m&sup2;</div><div className="text-xs text-slate-500">{t('property.area')}</div></div></div>
              {property.area > 0 && property.price > 0 && (
                <div className="flex items-center gap-2"><DollarSign className="w-5 h-5 text-[#D4AF37]" /><div><div className="font-semibold">${(property.price / property.area).toFixed(0)}/m&sup2;</div><div className="text-xs text-slate-500">USD/m&sup2;</div></div></div>
              )}
              {property.land_size > 0 && (
                <div className="flex items-center gap-2"><MapPin className="w-5 h-5 text-primary" /><div><div className="font-semibold">{property.land_size} m&sup2;</div><div className="text-xs text-slate-500">{language === 'es' ? 'Terreno' : 'Land'}</div></div></div>
              )}
            </div>
          )}
          {property.property_type === 'land' && property.area > 0 && (
            <div className="flex gap-6 py-4 border-y border-border">
              <div className="flex items-center gap-2"><Maximize className="w-5 h-5 text-primary" /><div><div className="font-semibold">{property.area} m&sup2;</div><div className="text-xs text-slate-500">{t('property.area')}</div></div></div>
              {property.price > 0 && (
                <div className="flex items-center gap-2"><DollarSign className="w-5 h-5 text-[#D4AF37]" /><div><div className="font-semibold">${(property.price / property.area).toFixed(0)}/m&sup2;</div><div className="text-xs text-slate-500">USD/m&sup2;</div></div></div>
              )}
            </div>
          )}

          {/* Description */}
          <div>
            <h2 className="font-heading text-lg font-semibold mb-3">{t('property.description')}</h2>
            <p className="text-slate-600 leading-relaxed whitespace-pre-wrap" data-testid="property-description">{property.description}</p>
          </div>

          {/* Property Verification Panel */}
          {(property.property_verified || propVerification?.verified) && (
            <div className="bg-gradient-to-br from-green-50 to-[#D4AF37]/5 border border-green-200 rounded-xl p-4" data-testid="verified-property-seal">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-green-700" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading font-semibold text-[#0B192C]">{language === 'es' ? 'Propiedad Verificada por WakaCasa' : 'WakaCasa-Verified Property'}</span>
                    <Badge className="bg-green-100 text-green-700 hover:bg-green-100 text-[10px]">{language === 'es' ? 'VERIFICADA' : 'VERIFIED'}</Badge>
                  </div>
                  <p className="text-xs text-slate-600 mt-1">{language === 'es' ? 'Documentos legales revisados por nuestro equipo (compra-venta, gravámenes, ficha catastral).' : 'Legal documents reviewed by our team (deed, encumbrance, cadastral).'}</p>
                </div>
              </div>
              {/* Premium / owner / admin: document previews */}
              {propVerification?.can_view_documents && propVerification?.documents && Object.keys(propVerification.documents).length > 0 && (
                <div className="mt-4 pt-4 border-t border-green-200" data-testid="verified-docs-preview">
                  <p className="text-xs uppercase tracking-wider text-slate-500 mb-2 font-semibold">{language === 'es' ? 'Documentos Verificados' : 'Verified Documents'}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {Object.entries(propVerification.documents).map(([key, doc]) => {
                      const meta = propVerification.documents_meta?.[key];
                      const label = language === 'es' ? meta?.label_es : meta?.label_en;
                      return (
                        <div key={key} className="flex items-center gap-2 text-xs bg-white rounded-lg p-2 border border-green-100" data-testid={`verified-doc-${key}`}>
                          <FileText className="w-3.5 h-3.5 text-[#D4AF37] flex-shrink-0" />
                          <span className="font-medium text-slate-700 truncate">{label || key}</span>
                          <Check className="w-3 h-3 text-green-600 ml-auto flex-shrink-0" />
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-[10px] text-slate-400 mt-2 italic">{language === 'es' ? 'Vista previa para compradores Premium. Descarga deshabilitada.' : 'Premium buyer preview. Download disabled.'}</p>
                </div>
              )}
              {/* Free buyers: locked teaser */}
              {!propVerification?.can_view_documents && propVerification?.uploaded_docs?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-green-200">
                  <p className="text-xs text-slate-600 inline-flex items-center gap-1.5">
                    <Lock className="w-3.5 h-3.5 text-[#D4AF37]" />
                    {language === 'es'
                      ? `${propVerification.uploaded_docs.length} documentos disponibles para compradores Premium.`
                      : `${propVerification.uploaded_docs.length} documents available to Premium buyers.`}
                  </p>
                  <Link to={isAuthenticated ? '/upgrade' : '/auth'}>
                    <Button size="sm" variant="outline" className="mt-2 h-8 border-[#D4AF37] text-[#0B192C] hover:bg-[#D4AF37]/10 gap-1.5" data-testid="upgrade-to-view-docs">
                      <Crown className="w-3.5 h-3.5 text-[#D4AF37]" />
                      {language === 'es' ? 'Mejorar para ver documentos' : 'Upgrade to view documents'}
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          )}

          {/* Owner CTA — Verify this property */}
          {user && (property.owner_id === user.id || ['admin', 'superadmin'].includes(user.role)) && propVerification && propVerification.status !== 'verified' && (
            <div className="bg-gradient-to-r from-[#D4AF37]/15 to-[#D4AF37]/5 border border-[#D4AF37]/40 rounded-xl p-4 flex items-center gap-3 flex-wrap" data-testid="owner-verify-cta">
              <ShieldCheck className="w-6 h-6 text-[#D4AF37] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-[#0B192C]">
                  {propVerification.status === 'under_review'
                    ? (language === 'es' ? 'Verificación en revisión' : 'Verification under review')
                    : propVerification.status === 'rejected'
                      ? (language === 'es' ? 'Verificación rechazada — revisa los comentarios' : 'Verification rejected — see notes')
                      : (language === 'es' ? '¿Quieres aumentar la confianza en tu propiedad?' : 'Want to boost trust in your listing?')}
                </p>
                <p className="text-xs text-slate-600">{language === 'es' ? 'Sube los documentos legales y obtén el sello WakaCasa-Verificado.' : 'Upload legal documents and earn the WakaCasa-Verified seal.'}</p>
              </div>
              <Link to={`/property/${id}/verify`}>
                <Button size="sm" className="bg-[#0B192C] hover:bg-[#1B2A4E] text-white gap-1.5" data-testid="owner-verify-cta-btn">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#D4AF37]" />
                  {propVerification.status === 'not_started'
                    ? (language === 'es' ? 'Verificar Propiedad' : 'Verify Property')
                    : (language === 'es' ? 'Continuar' : 'Continue')}
                </Button>
              </Link>
            </div>
          )}

          {/* Utilities & Services */}
          {(property.water_supply?.length > 0 || property.electricity?.length > 0 || property.connectivity?.length > 0) && (
            <div data-testid="property-utilities">
              <h2 className="font-heading text-lg font-semibold mb-3">{language === 'es' ? 'Servicios e Infraestructura' : 'Utilities & Services'}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {property.water_supply?.length > 0 && (
                  <div className="bg-blue-50/50 rounded-xl p-4 border border-blue-100" data-testid="utility-water">
                    <div className="flex items-center gap-2 mb-2">
                      <Droplets className="w-4 h-4 text-blue-600" />
                      <span className="font-semibold text-sm text-slate-700">{language === 'es' ? 'Agua' : 'Water'}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {property.water_supply.map(v => (
                        <span key={v} className="px-2.5 py-1 bg-white rounded-full text-[11px] font-medium text-blue-700 border border-blue-200">
                          {getUtilityLabel('water_supply', v, language)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {property.electricity?.length > 0 && (
                  <div className="bg-amber-50/50 rounded-xl p-4 border border-amber-100" data-testid="utility-electricity">
                    <div className="flex items-center gap-2 mb-2">
                      <Zap className="w-4 h-4 text-amber-600" />
                      <span className="font-semibold text-sm text-slate-700">{language === 'es' ? 'Electricidad' : 'Electricity'}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {property.electricity.map(v => (
                        <span key={v} className="px-2.5 py-1 bg-white rounded-full text-[11px] font-medium text-amber-700 border border-amber-200">
                          {getUtilityLabel('electricity', v, language)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {property.connectivity?.length > 0 && (
                  <div className="bg-green-50/50 rounded-xl p-4 border border-green-100" data-testid="utility-connectivity">
                    <div className="flex items-center gap-2 mb-2">
                      <Wifi className="w-4 h-4 text-green-600" />
                      <span className="font-semibold text-sm text-slate-700">{language === 'es' ? 'Conectividad' : 'Connectivity'}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {property.connectivity.map(v => (
                        <span key={v} className="px-2.5 py-1 bg-white rounded-full text-[11px] font-medium text-green-700 border border-green-200">
                          {getUtilityLabel('connectivity', v, language)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Map */}
          {property.latitude !== 0 && property.longitude !== 0 && (
            <div>
              <h2 className="font-heading text-lg font-semibold mb-3">{t('property.location')}</h2>
              <div className="h-[300px] rounded-xl overflow-hidden border border-border" data-testid="property-map">
                <MapView properties={[property]} center={[property.latitude, property.longitude]} zoom={14} selectedId={property.id} />
              </div>
              {property.address && <p className="text-sm text-slate-500 mt-2"><MapPin className="w-3.5 h-3.5 inline mr-1" />{property.address}</p>}
            </div>
          )}

          {/* Instagram embeds (lazy-loaded) */}
          <InstagramEmbeds urls={property.instagram_urls || []} />
        </div>

        {/* Sidebar - Contact */}
        <div className="space-y-4">
          {/* Owner/Agent Info */}
          {property.owner && (
            <div className="bg-white rounded-xl border border-border p-5" data-testid="owner-info">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center"><User className="w-6 h-6 text-primary" /></div>
                <div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {property.owner.role === 'agent' ? (
                      <Link to={`/agent/${property.owner_id}`} className="font-semibold text-sm text-slate-900 hover:text-primary hover:underline" data-testid="owner-name-link">
                        {property.owner.name}
                      </Link>
                    ) : (
                      <span className="font-semibold text-sm">{property.owner.name}</span>
                    )}
                    {ownerVerification?.verified && <VerifiedBadge role={property.owner.role} size="sm" />}
                    {property.owner.role === 'agent' && property.owner_trust && (
                      <TrustScore
                        data={{ ...property.owner_trust, breakdown: [], user_found: true }}
                        userId={property.owner_id}
                        variant="pill"
                        testIdPrefix="property-owner-trust"
                      />
                    )}
                  </div>
                  <div className="text-xs text-slate-500">{property.owner.role === 'agent' ? t('property.agent') : t('property.owner')}</div>
                </div>
              </div>
              {ownerVerification?.verified && (
                <div className="bg-green-50 rounded-lg p-3 mb-3 space-y-1.5" data-testid="owner-verification-stats">
                  <div className="flex items-center gap-1.5 text-xs text-green-700">
                    <ShieldCheck className="w-3.5 h-3.5" />
                    <span className="font-semibold">{language === 'es' ? 'Documentos Verificados' : 'Documents Verified'}</span>
                  </div>
                  {ownerVerification.completed_transactions > 0 && (
                    <p className="text-xs text-green-600">
                      {language === 'es'
                        ? `${ownerVerification.completed_transactions} transacciones completadas`
                        : `${ownerVerification.completed_transactions} transactions completed`}
                    </p>
                  )}
                </div>
              )}
              {property.contact_visible ? (
                <>
                  {property.owner.phone && <div className="flex items-center gap-2 text-sm text-slate-600" data-testid="owner-phone"><Phone className="w-3.5 h-3.5" />{property.owner.phone}</div>}
                  {property.owner.email && <div className="flex items-center gap-2 text-sm text-slate-600 mt-1" data-testid="owner-email"><Mail className="w-3.5 h-3.5" />{property.owner.email}</div>}
                  {/* Owner / admin / agency-mate: contact-reveal counter */}
                  {typeof property.contact_reveal_count === 'number' && (
                    <div className="mt-3 flex items-center gap-2 text-xs text-[#0B192C] bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5" data-testid="contact-reveal-count">
                      <Eye className="w-3.5 h-3.5 text-amber-700" />
                      <span className="font-medium">
                        {property.contact_reveal_count}
                      </span>
                      <span className="text-slate-600">
                        {language === 'es'
                          ? (property.contact_reveal_count === 1 ? 'persona vio el contacto' : 'personas vieron el contacto')
                          : (property.contact_reveal_count === 1 ? 'person viewed contact' : 'people viewed contact')}
                      </span>
                    </div>
                  )}
                </>
              ) : revealedContact ? (
                <>
                  {revealedContact.phone && <div className="flex items-center gap-2 text-sm text-slate-600" data-testid="owner-phone"><Phone className="w-3.5 h-3.5" />{revealedContact.phone}</div>}
                  {revealedContact.email && <div className="flex items-center gap-2 text-sm text-slate-600 mt-1" data-testid="owner-email"><Mail className="w-3.5 h-3.5" />{revealedContact.email}</div>}
                </>
              ) : (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="contact-locked">
                  <p className="text-xs text-slate-600 mb-2">
                    {language === 'es'
                      ? 'Haz clic para ver el correo y teléfono del agente.'
                      : "Click to see the agent's email and phone."}
                  </p>
                  <Button
                    size="sm"
                    onClick={handleRevealContact}
                    disabled={revealing}
                    className="w-full bg-[#0B192C] hover:bg-[#0B192C]/90 text-white gap-1.5 h-8"
                    data-testid="reveal-contact-btn"
                  >
                    {revealing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
                    {language === 'es' ? 'Mostrar Contacto' : 'Show Contact Details'}
                  </Button>
                </div>
              )}
              {property.agency && (
                <div className="mt-3 pt-3 border-t border-border">
                  <div className="text-xs text-slate-500 mb-1.5">{t('nav.agency')}</div>
                  <div
                    className="flex items-center gap-2.5"
                    data-testid="property-agency-link"
                  >
                    {property.agency.logo ? (
                      <img
                        src={property.agency.logo}
                        alt={property.agency.name}
                        className="w-9 h-9 rounded-lg object-cover border shrink-0"
                        data-testid="property-agency-logo"
                      />
                    ) : (
                      <div
                        className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                        style={{ backgroundColor: (property.agency.brand_color || '#0B192C') + '14' }}
                      >
                        <Building2 className="w-4 h-4" style={{ color: property.agency.brand_color || '#0B192C' }} />
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">{property.agency.name}</div>
                      {property.agency.tagline && (
                        <div className="text-[11px] italic text-slate-500 truncate">"{property.agency.tagline}"</div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {isAuthenticated && property.owner_id && (
                <Link to={`/book-tour/${property.id}/${property.owner_id}`} className="block mt-3">
                  <Button className="w-full bg-primary hover:bg-primary/90 text-white gap-1.5" data-testid="book-tour-calendar-btn">
                    <Video className="w-4 h-4" />{language === 'es' ? 'Agendar Tour Virtual' : 'Book Virtual Tour'}
                  </Button>
                </Link>
              )}
            </div>
          )}

          {/* Overseas Buyer Actions - only for users outside Venezuela */}
          {isAuthenticated && property.operation_type === 'sale' && user?.country && user.country !== 'Venezuela' && (
            <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl border border-primary/20 p-5 space-y-3" data-testid="buyer-actions">
              <h3 className="font-heading font-semibold text-sm">{language === 'es' ? 'Comprador Internacional' : 'International Buyer'}</h3>
              <Link to={`/book-tour/${property.id}/${property.owner_id}`}>
                <Button className="w-full bg-primary hover:bg-primary/90 text-white gap-1.5 mb-3" data-testid="request-tour-btn">
                  <Video className="w-4 h-4" />{language === 'es' ? 'Agendar Tour Virtual' : 'Book Virtual Tour'}
                </Button>
              </Link>
              <Button className="w-full bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] gap-1.5" data-testid="start-purchase-btn"
                onClick={async () => {
                  try {
                    const res = await axios.post(`${API}/transactions/start`, { property_id: property.id },
                      { headers: { Authorization: `Bearer ${token}` } });
                    toast.success(language === 'es' ? 'Compra iniciada!' : 'Purchase started!');
                    window.location.href = `/transaction/${res.data.transaction_id}`;
                  } catch { toast.error('Failed'); }
                }}>
                <FileText className="w-4 h-4" />{language === 'es' ? 'Iniciar Compra' : 'Start Purchase'}
              </Button>
            </div>
          )}

          {/* Contact Form */}
          <div className="bg-white rounded-xl border border-border p-5" data-testid="contact-form-section">
            <h3 className="font-heading font-semibold mb-3">{t('contact.title')}</h3>
            <form onSubmit={handleContact} className="space-y-3">
              <Input placeholder={t('contact.name')} value={contactForm.sender_name} required
                onChange={e => setContactForm(f => ({...f, sender_name: e.target.value}))}
                className="h-10 bg-slate-50" data-testid="contact-name" />
              <Input placeholder={t('contact.email')} type="email" value={contactForm.sender_email} required
                onChange={e => setContactForm(f => ({...f, sender_email: e.target.value}))}
                className="h-10 bg-slate-50" data-testid="contact-email" />
              <Input placeholder={t('contact.phone')} value={contactForm.sender_phone}
                onChange={e => setContactForm(f => ({...f, sender_phone: e.target.value}))}
                className="h-10 bg-slate-50" data-testid="contact-phone" />
              <Textarea placeholder={t('contact.message')} value={contactForm.message} required rows={4}
                onChange={e => setContactForm(f => ({...f, message: e.target.value}))}
                className="bg-slate-50" data-testid="contact-message" />
              <Button type="submit" disabled={sending} className="w-full bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] rounded-lg" data-testid="contact-send-btn">
                <Send className="w-4 h-4 mr-2" />{sending ? t('common.loading') : t('contact.send')}
              </Button>
            </form>
            <PlatformDisclaimer className="mt-4 text-slate-400" />
          </div>
          {/* Ad Space */}
          <AdBanner position="detail_sidebar" className="mt-4" />
        </div>
      </div>
    </div>
  );
}